import { useState, useCallback } from 'react';
import { getApiUrl } from '@/config/environment';
import { getAuthToken } from '@/core/auth/supabase-auth';
import { log } from '@/core/debug/anomalyLogger';
import { downloadAnomalyInfoFile } from './infoFileGenerator';
import type { AnomalyState } from './anomalyReducer';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export interface UseAnomalyDownload {
  downloadUrl: string | null;
  downloading: boolean;
  downloadError: string | null;
  handleDownload: () => Promise<void>;
  clearDownloadError: () => void;
}

export function useAnomalyDownload(state: AnomalyState, t: TFn): UseAnomalyDownload {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { uploadId, processedCsvFilename } = state;
  const downloadUrl =
    uploadId && processedCsvFilename
      ? getApiUrl(
          `/api/adjustmentsOfData/processed/${encodeURIComponent(uploadId)}/${encodeURIComponent(processedCsvFilename)}`,
        )
      : null;

  const handleDownload = useCallback(async () => {
    if (!downloadUrl || downloading) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      const csvFilename = processedCsvFilename ?? 'processed.csv';
      a.download = csvFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);

      // Sibling info file — matches W6/W7/W8 convention.
      downloadAnomalyInfoFile(state, csvFilename, t);
    } catch (e) {
      log.err('useAnomalyDownload.download', e);
      setDownloadError(
        t('anomaly.errors.downloadFailed', {
          defaultValue: 'Could not download the file. Please try again.',
        }),
      );
    } finally {
      setDownloading(false);
    }
  }, [downloadUrl, downloading, processedCsvFilename, state, t]);

  const clearDownloadError = useCallback(() => setDownloadError(null), []);

  return { downloadUrl, downloading, downloadError, handleDownload, clearDownloadError };
}

export default useAnomalyDownload;
